export type EntityId = string;

export type AbilityKey = 'str' | 'dex' | 'con' | 'int' | 'wis' | 'cha';
export type ActionType = 'attack' | 'save' | 'utility';
export type ActionSection = 'trait' | 'action' | 'reaction' | 'bonus' | 'legendary';
export type SaveOutcome = 'fail' | 'half' | 'zero';
export type RollMode = 'auto' | 'normal' | 'advantage' | 'disadvantage';

export interface AbilityScores {
  str: number;
  dex: number;
  con: number;
  int: number;
  wis: number;
  cha: number;
}

export interface DamagePart {
  dice: string;
  type: string;
}

export interface DamageModifiers {
  damage: string[];
  conditions: string[];
}

export interface ActionDefinition {
  id: EntityId;
  name: string;
  type: ActionType;
  section?: ActionSection;
  note?: string;
  range?: string;
  attackBonus?: number;
  damages: DamagePart[];
  saveAbility?: AbilityKey;
  saveDC?: number;
  onSuccess?: 'half' | 'zero';
  recharge?: number;
  onHitStatus?: ConditionKey;
  onHitStatusRounds?: number;
  onHitSaveAbility?: AbilityKey;
  onHitSaveDC?: number;
}

export interface Ability {
  id: EntityId;
  name: string;
  description: string;
}

export interface Monster {
  id: EntityId;
  name: string;
  avatar?: string;
  backgroundImage?: string;
  size?: string;
  creatureTypeText?: string;
  subtypeText?: string;
  alignment?: string;
  cr: number;
  types: string[];
  ac: number;
  hp: { average: number; roll: string };
  speed: Record<string, number>;
  abilities: AbilityScores;
  resistances: DamageModifiers;
  vulnerabilities: DamageModifiers;
  immunities: DamageModifiers;
  actions: ActionDefinition[];
  features?: string;
  isDefault?: boolean;
}

export interface PlayerCharacter {
  id: EntityId;
  name: string;
  avatar?: string;
  backgroundImage?: string;
  metaLine?: string;
  ac: number;
  hpMax: number;
  hpCurrent: number;
  abilities: AbilityScores;
  resistances: DamageModifiers;
  vulnerabilities: DamageModifiers;
  immunities: DamageModifiers;
  actions: ActionDefinition[];
  features?: string;
  isDefault?: boolean;
}

export interface EncounterGroupItem {
  monsterId: EntityId;
  count: number;
}

export interface EncounterGroup {
  id: EntityId;
  name: string;
  monsters: EncounterGroupItem[];
}

export type ConditionKey =
  | 'prone'
  | 'restrained'
  | 'blinded'
  | 'poisoned'
  | 'charmed'
  | 'frightened'
  | 'deafened'
  | 'grappled'
  | 'incapacitated'
  | 'invisible'
  | 'paralyzed'
  | 'petrified'
  | 'stunned'
  | 'unconscious'
  | 'exhaustion';

export interface ConditionInstance {
  id: EntityId;
  key: ConditionKey;
  rounds: number;
  sourceUid?: EntityId;
  level?: number;
  stepRounds?: number;
}

export interface CombatAction extends ActionDefinition {
  cooldown: number;
}

export interface Combatant {
  uid: EntityId;
  baseId?: EntityId;
  kind: 'monster' | 'pc';
  name: string;
  avatar?: string;
  ac: number;
  baseMaxHp: number;
  hpMax: number;
  hpCurrent: number;
  tempHp: number;
  abilities: AbilityScores;
  resistances: DamageModifiers;
  vulnerabilities: DamageModifiers;
  immunities: DamageModifiers;
  actions: CombatAction[];
  statuses: ConditionInstance[];
  initiative: number | null;
  initiativeRoll?: number;
  initiativeModifier?: number;
  justJoined?: boolean;
}

export interface BattleState {
  participants: Combatant[];
  currentIndex: number;
  round: number;
}

export type DecisionRequest =
  | {
      id: EntityId;
      kind: 'withinFiveFeet';
      purpose: 'proneRoll' | 'criticalHit';
      targetUid: EntityId;
      title: string;
      message: string;
    }
  | {
      id: EntityId;
      kind: 'fearSourceVisible';
      targetUid: EntityId;
      title: string;
      message: string;
    }
  | {
      id: EntityId;
      kind: 'saveOutcome';
      targetUid: EntityId;
      title: string;
      message: string;
      autoFail: boolean;
      hasDisadvantage: boolean;
    };

export interface DamageResult {
  rawAmount: number;
  finalAmount: number;
  type: string;
  modifier: 'normal' | 'resistant' | 'vulnerable' | 'immune';
}

export interface RolledDamagePart {
  amount: number;
  type: string;
}

export interface TargetResolution {
  targetUid: EntityId;
  outcome: 'hit' | 'miss' | 'critical' | 'critical-failure' | 'save-fail' | 'save-success';
  attackRoll?: number;
  attackTotal?: number;
  damages: DamageResult[];
}

export interface ActionResolutionSession {
  id: EntityId;
  stage: 'idle' | 'awaitingDecision' | 'awaitingSaveOutcome' | 'presentingResults' | 'complete';
  actorUid: EntityId;
  action: ActionDefinition;
  targetUids: EntityId[];
  rollMode: RollMode;
  currentTargetIndex: number;
  pendingDecision?: DecisionRequest;
  decisionRequests: DecisionRequest[];
  decisionAnswers: Record<EntityId, boolean>;
  currentDecisionIndex: number;
  rolledDamages: RolledDamagePart[];
  results: TargetResolution[];
}

export const defaultAbilities = (): AbilityScores => ({
  str: 10,
  dex: 10,
  con: 10,
  int: 10,
  wis: 10,
  cha: 10,
});

export const emptyModifiers = (): DamageModifiers => ({ damage: [], conditions: [] });
